import { useToast } from 'vue-toastification'

import { useCartConfigurationStore } from '@/stores/cartConfiguration'

import { sortBy } from 'lodash-es'

import constants from '@/lib/constants'

import { CadyMarket } from '@/types/CadyMarket'
import { CatalogBundleStorefrontDtoWithContent } from '@/types/CatalogBundleStorefrontDtoWithContent'
import { type ContextKey, FALLBACK_CONTEXT_KEY, normalizeContextLabel } from '@/types/ContextKey'
import { MediaLiteCanonical } from '@/types/MediaLiteCanonical'

import { CatalogFlowBundleType } from '@VelocitySquared/sunny16-commerce-dataobjects/enums'
import {
  CartBundleStorefrontDto,
  CartBundleUpsertStorefrontDto,
  CatalogCampaignStorefrontDto,
  CatalogFlowStepBundleStorefrontDto,
  CatalogPromotionStorefrontDto,
  ItemGroupStorefrontDto,
  MediaReferenceDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

import type { ContentfulEntry } from '#contentful/ContentfulEntrySys'
import type { ContentfulFeatureItemAlpha } from '#contentful/ContentfulFeatureItemAlpha'
import type { ContentfulFeatureItemBravo } from '#contentful/ContentfulFeatureItemBravo'
import type { ContentfulNotification } from '#contentful/ContentfulNotification'

import { feedbackContent } from '@/content/feedback'

export function obfuscateMediaId(mediaId: number): string {
  const mediaIdSnipped = mediaId.toString().slice(-4)
  return `xxxx${mediaIdSnipped}`
}

export function sortBundlesByZOrder(
  bundles: CatalogBundleStorefrontDtoWithContent[],
  flowStepBundles: CatalogFlowStepBundleStorefrontDto[],
  bundleType: CatalogFlowBundleType
): CatalogBundleStorefrontDtoWithContent[] {
  return bundles.sort((a, b) => {
    const bundleA = flowStepBundles.find(
      (cfsb) => cfsb.type === bundleType && cfsb.catalogBundleId === a.catalogBundleId
    )

    const bundleB = flowStepBundles.find(
      (cfsb) => cfsb.type === bundleType && cfsb.catalogBundleId === b.catalogBundleId
    )

    return (bundleA ? bundleA.zOrder : 0) - (bundleB ? bundleB.zOrder : 0)
  })
}

export function mapCartBundleToCartBundleUpsert(
  cartBundle: CartBundleStorefrontDto
): CartBundleUpsertStorefrontDto {
  const cartBundleUpsert: CartBundleUpsertStorefrontDto = {
    cartBundleId: cartBundle.cartBundleId,
    catalogBundleId: cartBundle.catalogBundleId,
    quantity: cartBundle.quantity,
    shippingAddressId: cartBundle.shippingAddressId,
    // discountReason: cartBundle.discountReason,
    sourcePromotionId: cartBundle.sourcePromotionId,
    requiredParentCartBundleId: cartBundle.requiredParentCartBundleId,
    selectedItems: cartBundle.cartItems.map((cartItem) => ({
      itemGroupItemId: cartItem.itemGroupItemId,
      itemId: cartItem.itemId,
      quantity: cartItem.quantity,
      cartItemCustomizationDetails: cartItem.cartItemCustomizationDetails
    })),
    // `cartAttributes` is a request-only field — the server never echoes it back on `CartBundleStorefrontDto`, and the cart store overwrites it with the `cadySessionToken` on every upsert, so `null` here is intentional.
    // Do NOT repopulate it from `cartBundle.attributes`: that round-trip (added only to satisfy type-check) fed nothing, since `.attributes` is a separate server-managed collection.
    cartAttributes: null
  }

  return cartBundleUpsert
}
export const stripeAppearanceRules = {
  '.Label': {
    fontFamily: "'Roboto', 'Helvetica Neue', Arial, Verdana, sans-serif",
    fontWeight: 700,
    fontSize: '0.875rem',
    marginBottom: '0.5rem',
    marginLeft: '0.5rem'
  }
}

export function sumCartBundlesTotals(bundles: CartBundleStorefrontDto[]): number {
  return bundles.reduce((acc, bundle) => acc + bundle.bundleTotalPostDiscounts, 0)
}

export function resolveCadyMarket(marketString: string): CadyMarket | undefined {
  return Object.values(CadyMarket).find((market) => market === marketString) || undefined
}

export function sortMediaItemsByMediaTags(mediaItems: MediaLiteCanonical[]): MediaLiteCanonical[] {
  const mediaTagsToSortLast = ['panoramic', 'group', 'team']

  return sortBy(mediaItems, [
    (gmi: MediaLiteCanonical) => {
      return gmi.MediaTags?.some((mt) => mediaTagsToSortLast.includes(mt)) ? 1 : 0
    },
    'MediaId'
  ])
}

// True when the feature item is gated on the sentinel `FALLBACK_CONTEXT_KEY`.
// Used by both the context-filter exemption and the fallback-partition step
// in `getAvailableFeatureItemsByDependencies`.
function isFallbackContextItem(
  fi: ContentfulEntry<ContentfulFeatureItemAlpha | ContentfulFeatureItemBravo>
): boolean {
  return (
    fi.fields.dependsOnContext !== undefined &&
    normalizeContextLabel(fi.fields.dependsOnContext) === FALLBACK_CONTEXT_KEY
  )
}

export function getAvailableFeatureItemsByDependencies(
  featureItems: ContentfulEntry<ContentfulFeatureItemAlpha | ContentfulFeatureItemBravo>[],
  activeCampaigns: CatalogCampaignStorefrontDto[] | null | undefined,
  availablePromos: CatalogPromotionStorefrontDto[] | null | undefined,
  activeContexts: ContextKey[] = []
): ContentfulEntry<ContentfulFeatureItemAlpha | ContentfulFeatureItemBravo>[] | null {
  if (!featureItems || featureItems.length === 0) {
    return null
  }

  // This function filters the feature items based on the available dependencies.

  const activeCampaignGuids: string[] = []

  if (activeCampaigns && activeCampaigns.length > 0) {
    activeCampaignGuids.push(
      ...activeCampaigns.map((campaign: CatalogCampaignStorefrontDto) => campaign.campaignId)
    )
  }

  featureItems = featureItems.filter((fi) => {
    // check to ensure the feature item has fields. items returned in Draft status do not have fields
    if (!fi.fields) {
      return false
    }

    if (fi.fields.participatesInCampaigns !== undefined) {
      // If the feature item has a participatesInCampaigns field, we check if the activeCampaignGuids array includes the participatesInCampaigns linking GUID
      return fi.fields.participatesInCampaigns.some(
        (c) =>
          c.fields.sunny16Guid && activeCampaignGuids.includes(c.fields.sunny16Guid.toLowerCase())
      )
    }

    // If there is no participatesInCampaigns field, we assume the feature item is available
    return true
  })

  const availablePromoGuids: string[] = []

  if (availablePromos && availablePromos.length > 0) {
    // Map the availablePromo id GUIDs to an array of strings
    availablePromoGuids.push(
      ...availablePromos.map((promo: CatalogPromotionStorefrontDto) => promo.promotionId)
    )
  }

  featureItems = featureItems.filter((fi) => {
    if (fi.fields.dependsOnPromotion !== undefined) {
      // If the feature item has a dependsOnPromotion field, we check if the availablePromoGuids array includes the dependsOnPromotion linking GUID
      return availablePromoGuids.includes(
        fi.fields.dependsOnPromotion.fields.sunny16Guid.toLowerCase()
      )
    }

    // If there is no dependsOnPromotion field, we assume the feature item is available
    return true
  })

  // Gate on the `dependsOnContext` field — drops items whose normalized
  // Contentful dropdown value isn't in the caller-supplied active-contexts
  // list. Items keyed on the `FALLBACK_CONTEXT_KEY` are EXEMPTED from this
  // check; their fate is decided by the partition step below. Fail-closed:
  // an unrecognized or typo'd non-fallback label won't match anything active
  // and the item drops out, which is safer than rendering misconfigured
  // entries.
  featureItems = featureItems.filter((fi) => {
    if (fi.fields.dependsOnContext === undefined) return true
    if (isFallbackContextItem(fi)) return true
    return (activeContexts as string[]).includes(normalizeContextLabel(fi.fields.dependsOnContext))
  })

  // Fallback partition — items keyed on `'fallback'` render ONLY when no
  // other items survived the filter. If any non-fallback items survived,
  // the fallbacks are dropped; otherwise the fallbacks become the result.
  const fallbacks: typeof featureItems = []
  const nonFallbacks: typeof featureItems = []
  for (const fi of featureItems) {
    if (isFallbackContextItem(fi)) {
      fallbacks.push(fi)
    } else {
      nonFallbacks.push(fi)
    }
  }
  featureItems = nonFallbacks.length > 0 ? nonFallbacks : fallbacks

  if (featureItems.length === 0) {
    return null
  }

  return featureItems
}

export function getAvailableNotificationForDisplay(
  notifications: ContentfulEntry<ContentfulNotification>[] | null | undefined,
  activeCampaigns: CatalogCampaignStorefrontDto[] | null | undefined,
  dismissedNotifications: string[]
): ContentfulEntry<ContentfulNotification> | null | undefined {
  if (!notifications || notifications.length === 0) {
    return null
  }

  const activeCampaignGuids: string[] = activeCampaigns
    ? activeCampaigns.map((ac) => ac.campaignId)
    : []

  const availableNotifications = notifications.filter((n) => {
    // Map the sunny16 GUIDs of the campaigns the notification participates in to an array of strings, transformed to lower case
    const notificationParticipatesInCampaignIds = n.fields.participatesInCampaigns
      ? n.fields.participatesInCampaigns
          .filter((c) => c.fields.sunny16Guid)
          .map((c) => c.fields.sunny16Guid!.toLowerCase())
      : []

    return (
      // If the notification does not participate in any campaigns, it is available
      (notificationParticipatesInCampaignIds.length === 0 ||
        // If the notification participates in campaigns, check if any of the active campaign GUIDs match
        notificationParticipatesInCampaignIds.some((campaignId) => {
          return activeCampaignGuids.includes(campaignId)
        })) &&
      !dismissedNotifications.includes(n.sys.id)
    )
  })

  // For now, just return the first available notification
  // In the future, we may want to return all available notifications so they can be cycled through
  if (availableNotifications.length > 0) {
    return availableNotifications[0]
  }
}

export function findUnderclassBackgroundImageUrl(backgroundId: string): string | null {
  const background = constants.underclassBackgroundData.find((bg) => bg.id === backgroundId)

  return background ? background.imageUrl : null
}

export function findItemGroupItemQuantity(
  itemGroups: ItemGroupStorefrontDto[] | null | undefined,
  itemGroupItemId: string | null | undefined
): number | null {
  if (!itemGroups || !itemGroupItemId) {
    return null
  }

  for (const itemGroup of itemGroups) {
    const itemGroupItem = itemGroup.itemGroupItems?.find(
      (igi) => igi.itemGroupItemId === itemGroupItemId
    )

    if (itemGroupItem) {
      return itemGroupItem.quantity > 0 ? itemGroupItem.quantity : null
    }
  }

  return null
}

export function sumItemGroupItemQuantities(
  itemGroups: ItemGroupStorefrontDto[] | null | undefined,
  itemGroupItemIds: (string | null | undefined)[]
): number | null {
  const quantities = itemGroupItemIds
    .map((itemGroupItemId) => findItemGroupItemQuantity(itemGroups, itemGroupItemId))
    .filter((quantity): quantity is number => quantity !== null)

  return quantities.length > 0 ? quantities.reduce((total, quantity) => total + quantity, 0) : null
}

export function updatePromptValue(
  cartConfigurationStore: ReturnType<typeof useCartConfigurationStore>,
  toast: ReturnType<typeof useToast>,
  cartItemIds: string[],
  promptId: string,
  value: string | MediaReferenceDto
): boolean {
  try {
    const result = cartConfigurationStore.updatePromptValue(cartItemIds, promptId, value)

    if (!result && value instanceof MediaReferenceDto) {
      toast.error(feedbackContent.maxPosesReached)
    } else if (!result) {
      toast.error(feedbackContent.updatePromptValueFailed)
    }

    return result
  } catch (error) {
    console.error('Error updating prompt value:', error)
    toast.error(feedbackContent.updatePromptValueFailed)
    return false
  }
}

export function testForCityStateZipInValue(value: string): boolean {
  if (!value) return false
  /*
    ##REGEX EXPLANATION##
    - A street number (\d+)
    - A street name ([\w\s\.]+)
    - Optional comma and spaces
    - A city name ([\w\s\.]+)
    - Optional comma and spaces
    - A state (either two uppercase letters or a full name)
    - Spaces
    - A zipcode (5 digits, optional dash and 4 digits)
    - Optionally, a country or extra info at the ends
   */
  const cityStateOrZipRegex =
    /\d+\s+[\w\s\.]+(?:,)?\s*[\w\s\.]+(?:,)?\s*(?:[A-Z]{2}|[A-Za-z\s]+)\s*\d{5}(?:-\d{4})?(?:,?\s*[\w\s\.]+)?/i
  return cityStateOrZipRegex.test(value)
}

export function formatCurrency(value: number): string {
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

// Rounds down rather than to nearest, EXCEPT when under a dollar away — there, floor would show
// "$0", which reads as already unlocked. In that sub-dollar case we round up to "$1" instead so an
// "away from" amount never displays as zero while still requiring more to unlock.
export function formatWholeDollarCurrency(value: number): string {
  const cents = Math.round(value * 100) / 100
  const rounded = cents > 0 && cents < 1 ? 1 : Math.floor(cents)
  return `$${rounded.toLocaleString('en-US', {
    maximumFractionDigits: 0
  })}`
}

export function formatCurrencyWithSign(value: number): string {
  const formatted = formatCurrency(Math.abs(value))
  return value < 0 ? `-${formatted}` : `+${formatted}`
}
